import type { GetJepxMemberInfoResponse } from '@ebalance/proto/bffuser/v1/jepx_member_info_service_pb'
import type { Decimal } from '@ebalance/proto/google/type/decimal_pb'

import { JEPX_PRICE_CONSTRAINT } from '~/models/numberInputConstraint'

/** 値が無いセルの表示 (mock-app `/jepx-master` と同じ em dash)。 */
export const NO_VALUE = '—'

/**
 * 取引手数料の単価表示。 `google.type.Decimal` は文字列で運ばれるので、
 * 数値化せず小数第 3 位までの桁を整えるだけにする (= 二進浮動小数を挟まない)。
 *
 * 単価の SoT は JEPX 会員情報管理 (`docs/specs/admin/integration/jepx-member-info.md`
 * AC-IJM-007a) で、 JEPX 会員情報管理画面 (料金・手数料) と入札制限画面
 * (手数料自動考慮カードの案内、 AC-BDL-017 / AC-BDL-020) の 2 page が同じ整形で
 * 表示する。 page 固有 model が複数 page から使われたので共有 model へ昇格した
 * (`frontend/apps/ebalance/CLAUDE.md`)。
 */
export function formatFeePerKwh(value: Decimal | undefined): string {
  if (value === undefined || value.value === '') {
    return NO_VALUE
  }
  const [intPart, fracPart = ''] = value.value.split('.')
  return `${intPart}.${fracPart.padEnd(3, '0')}`
}

export type JepxFeeMarket = 'spot' | 'intraday'

/**
 * 会員情報一括取得 (`GetJepxMemberInfo`) の応答から、 市場ごとの取引手数料単価
 * (AC-IJM-007a) を取り出す。 「単価が存在しない」の判定はここ 1 か所に置く。
 *
 * - JEPX 会員 ID が未登録の組織 (AC-IJM-003、 `memberInfo` が無い) は単価が
 *   存在しないものとして扱う (`bidding/spot-bidding.md` AC-BSP-100)。
 *   `feeSchedule` は会員未登録でも常に返るため、 ここで明示的に弾く
 * - 未取得 (`response` が undefined) ・空文字も undefined
 */
export function resolveFeeUnitPrice(
  response: GetJepxMemberInfoResponse | undefined,
  market: JepxFeeMarket,
): Decimal | undefined {
  if (response?.memberInfo === undefined) {
    return undefined
  }
  return feeScheduleUnitPrice(response, market)
}

/**
 * 参加料規程 (`feeSchedule`) の市場ごとの取引手数料単価を、 JEPX 会員 ID の登録に
 * 依らず取り出す。 自動入札の発火は会員登録に依らず現行の単価表で加減算する
 * (backend `FeeAutoConsiderResolver.AutoBidSnapshotFromSettings`、 AC-APF-068) ので、
 * 承認フロー画面の自動入札ルールの表示はこちらを使う。 未取得・空文字は undefined。
 */
export function feeScheduleUnitPrice(
  response: GetJepxMemberInfoResponse | undefined,
  market: JepxFeeMarket,
): Decimal | undefined {
  const fee =
    market === 'spot'
      ? response?.feeSchedule?.spotTradingFeePerKwh
      : response?.feeSchedule?.intradayTradingFeePerKwh
  if (fee === undefined || fee.value === '') {
    return undefined
  }
  return fee
}

/** JEPX の入札価格の値域 (`docs/specs/bidding/bid-limits.md` 「0.01〜999 円/kWh」) を 0.01 円単位で持つ。 */
const JEPX_MIN_PRICE_CENTS = Math.round(JEPX_PRICE_CONSTRAINT.min * 100)
const JEPX_MAX_PRICE_CENTS = Math.round(JEPX_PRICE_CONSTRAINT.max * 100)
/** 0 以上で、 JEPX の価格最小単位 0.01 円/kWh の整数倍の小数 (小数第 3 位以下は 0 だけ)。 */
const FEE_IN_PRICE_UNIT = /^(\d+)(?:\.(\d{1,2})0*)?$/

export type FeeAdjustedPriceBoundsExample = {
  /** 上下限価格 未設定時の、買いの希望価格の下限 (= 0.01 + 手数料単価)。 */
  minBuyPrice: string
  /** 上下限価格 未設定時の、売りの希望価格の上限 (= 999 − 手数料単価)。 */
  maxSellPrice: string
}

/**
 * 「0.01 + 手数料単価」「999 − 手数料単価」を算出する
 * (`docs/specs/bidding/bid-limits.md` AC-BDL-021 常時注釈 4 行目の例)。
 *
 * 発注側の入札価格の算出 (backend `bidding.ComputeFeeAdjustedOrderPrice`) と同じく、
 * 手数料単価は 0 以上で 0.01 円/kWh の整数倍のときだけ使い
 * (`docs/specs/admin/integration/jepx-member-info.md` 「決定の経緯: 取引手数料単価の spec 内明記」)、
 * 0.01 円単位の整数で加減算する。 未取得・空文字・端数のある単価・想定外の形式は
 * 発注側でも入札価格を算出できないため undefined を返す (呼び出し側で「算出できない」旨の
 * 文言に切り替える)。
 */
export function computeFeeAdjustedPriceBoundsExample(
  feeUnitPrice: Decimal | undefined,
): FeeAdjustedPriceBoundsExample | undefined {
  const feeCents = toPriceUnitCents(feeUnitPrice)
  if (feeCents === undefined) {
    return undefined
  }
  return {
    minBuyPrice: formatPriceCents(JEPX_MIN_PRICE_CENTS + feeCents),
    maxSellPrice: formatPriceCents(JEPX_MAX_PRICE_CENTS - feeCents),
  }
}

/**
 * 手数料自動考慮で入札価格の加減算・希望価格の逆算に使う取引手数料単価 (円/kWh) を数値で返す。
 *
 * 発注側の入札価格の算出 (backend `bidding.ComputeFeeAdjustedOrderPrice`) と同じく、 0 以上で
 * 0.01 円/kWh の整数倍の単価だけを使う。 未取得・空文字・端数のある単価・想定外の形式は undefined
 * (= 使える単価が無い) を返す。 端数のある単価を 0.01 円単位に丸めて使うと、 画面が出す価格と
 * backend の取込 (同じ単価を使えないものとして取込を止める) が食い違う。
 */
export function feeUnitPriceToYenPerKwh(
  value: Decimal | undefined,
): number | undefined {
  const cents = toPriceUnitCents(value)
  return cents === undefined ? undefined : cents / 100
}

function toPriceUnitCents(value: Decimal | undefined): number | undefined {
  const match = value === undefined ? null : FEE_IN_PRICE_UNIT.exec(value.value)
  if (match === null) {
    return undefined
  }
  const [, intPart, fracPart = ''] = match
  return Number(intPart) * 100 + Number(fracPart.padEnd(2, '0'))
}

/** 表示は formatFeePerKwh と同じ「小数第 3 位まで」の桁数に揃える。 */
function formatPriceCents(cents: number): string {
  return (cents / 100).toFixed(3)
}
